import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
  StudentCourseListItem,
  StudentEnrolmentStatus,
  StudentFormSubmission,
} from '@/models/current-student-advanced-standing-form/api'

export const defaultHandbookUrl = 'http://www.latrobe.edu.au/handbook/current'
export const invalidCourseFallbackMessage = 'Please choose a valid La Trobe course.'
export const missingEnrolmentStatusMessage = 'Please choose your enrolment status.'

export function isStudentEnrolmentStatus(value: unknown): value is StudentEnrolmentStatus {
  return value === 'Commencing' || value === 'Continuing'
}

export function courseIsSelectable(course: StudentCourseListItem): boolean {
  return !['WDE', 'APL'].includes(course.course_admission_status_code)
}

export const useCurrentStudentCourseSelectionStore = defineStore('currentStudentAdvancedStandingFormCourseSelection', () => {
  const courseList = ref<StudentCourseListItem[]>([])
  const selectedCourseId = ref('')
  const enrolmentStatus = ref<StudentEnrolmentStatus | ''>('')
  const sponsoredStudent = ref('')
  const invalidCourseMessage = ref('')
  const invalidEnrolmentStatusMessage = ref('')
  const internationalStudentError = ref(false)

  const selectableCourses = computed(() => {
    return courseList.value.filter(courseIsSelectable)
  })

  const selectedCourse = computed(() => {
    return selectableCourses.value.find((course) => {
      return String(course.student_course_id) === selectedCourseId.value
    }) ?? null
  })

  const isInternationalCourse = computed(() => {
    return selectedCourse.value?.course_liability_is_international === true
  })

  const isSponsoredStudentLocked = computed(() => {
    return selectedCourse.value?.sponsored_student === true
  })

  const isSponsoredStudent = computed(() => {
    return sponsoredStudent.value === '1'
  })

  const handbookUrl = computed(() => {
    return selectedCourse.value?.handbook_url || defaultHandbookUrl
  })

  const showSpgTool = computed(() => {
    return (selectedCourse.value?.course_year ?? 0) >= 2022
  })

  const courseSelectClass = computed(() => ({
    errorInput: invalidCourseMessage.value !== '',
  }))

  const enrolmentStatusClass = computed(() => ({
    errorInput: invalidEnrolmentStatusMessage.value !== '',
  }))

  const internationalStudentClass = computed(() => ({
    errorInput: internationalStudentError.value,
  }))

  function reset(): void {
    courseList.value = []
    selectedCourseId.value = ''
    enrolmentStatus.value = ''
    sponsoredStudent.value = ''
    invalidCourseMessage.value = ''
    invalidEnrolmentStatusMessage.value = ''
    internationalStudentError.value = false
  }

  function loadCourseListing(courses: StudentCourseListItem[], submission: StudentFormSubmission): void {
    courseList.value = courses

    const selectedFromSubmission = submission.student_course_id == null
      ? null
      : selectableCourses.value.find((course) => course.student_course_id === Number(submission.student_course_id))
    const firstValidCourse = selectableCourses.value.find((course) => course.invalid_stage_error_message === null)
    const initialCourse = selectedFromSubmission ?? firstValidCourse ?? selectableCourses.value[0] ?? null

    selectedCourseId.value = initialCourse ? String(initialCourse.student_course_id) : ''
    enrolmentStatus.value = isStudentEnrolmentStatus(submission.student_enrolment_status)
      ? submission.student_enrolment_status
      : ''
    sponsoredStudent.value = submission.sponsored_student == null
      ? sponsoredStudent.value
      : (submission.sponsored_student ? '1' : '0')

    applySelectedCourseState()
  }

  function onCourseChange(): void {
    invalidCourseMessage.value = ''
    applySelectedCourseState()
  }

  function onEnrolmentStatusChange(): void {
    invalidEnrolmentStatusMessage.value = ''
  }

  function onSponsoredStudentChange(): void {
    internationalStudentError.value = false
    applySelectedCourseState()
  }

  function applySelectedCourseState(): void {
    invalidCourseMessage.value = selectedCourse.value?.invalid_stage_error_message ?? ''

    if (isSponsoredStudentLocked.value) {
      sponsoredStudent.value = '1'
    }
  }

  function validateCourseInput(): boolean {
    if (selectedCourse.value == null || selectedCourse.value.invalid_stage_error_message !== null) {
      invalidCourseMessage.value = selectedCourse.value?.invalid_stage_error_message || invalidCourseFallbackMessage
      return false
    }

    invalidCourseMessage.value = ''
    return true
  }

  function validateEnrolmentStatus(): boolean {
    if (!enrolmentStatus.value) {
      invalidEnrolmentStatusMessage.value = missingEnrolmentStatusMessage
      return false
    }

    invalidEnrolmentStatusMessage.value = ''
    return true
  }

  function validateInternationalUnder18Input(): boolean {
    internationalStudentError.value = sponsoredStudent.value === '' && isInternationalCourse.value
    return !internationalStudentError.value
  }

  return {
    courseList,
    selectedCourseId,
    enrolmentStatus,
    sponsoredStudent,
    invalidCourseMessage,
    invalidEnrolmentStatusMessage,
    internationalStudentError,
    selectableCourses,
    selectedCourse,
    isInternationalCourse,
    isSponsoredStudentLocked,
    isSponsoredStudent,
    handbookUrl,
    showSpgTool,
    courseSelectClass,
    enrolmentStatusClass,
    internationalStudentClass,
    reset,
    loadCourseListing,
    onCourseChange,
    onEnrolmentStatusChange,
    onSponsoredStudentChange,
    applySelectedCourseState,
    validateCourseInput,
    validateEnrolmentStatus,
    validateInternationalUnder18Input,
  }
})
